/* eslint-disable react/jsx-props-no-spreading */
import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import { Box } from '@altermaind/web/ui';
import { DrawerProps } from './types';

type Props = DrawerProps & {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

export const Drawer: FunctionComponent<Props> = ({
  children,
  isOpen,
  width,
  placement = 'right',
  closeOnClickOutSide = true,
  withShadow = false,
  containerProps = {},
  setIsOpen,
  style,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleClickOutside = (e: any) => {
      if (ref && ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleEsc = (e: any) => {
      if (e?.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (closeOnClickOutSide) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    document.addEventListener('keydown', handleEsc);

    return () => {
      if (closeOnClickOutSide) {
        document.removeEventListener('mousedown', handleClickOutside);
      }
      document.removeEventListener('keydown', handleEsc);
    };
  }, [closeOnClickOutSide, ref, setIsOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  return (
    <>
      <Box
        ref={ref}
        position='fixed'
        top={0}
        left={placement === 'left' ? 0 : undefined}
        right={placement === 'right' ? 0 : undefined}
        zIndex={10}
        height='100vh'
        width={width}
        backgroundColor='primary/background'
        boxShadow={
          withShadow
            ? `${
                placement === 'left' ? '5px' : '-5px'
              } 0px 10px 0px rgba(40, 40, 40, 1)`
            : undefined
        }
        px='s'
        style={{
          transform: `translateX(${
            // eslint-disable-next-line no-nested-ternary
            isOpen ? '0' : placement === 'right' ? '100%' : '-100%'
          })`,
          transition: 'transform 0.2s linear',
          ...style,
        }}
      >
        <Box
          style={{ boxSizing: 'border-box' }}
          py='s'
          height='100vh'
          overflowY='auto'
          {...containerProps}
        >
          {children}
        </Box>
      </Box>
      {isOpen && (
        <Box
          position='fixed'
          zIndex={9}
          style={{ inset: 0 }}
          backgroundColor='black'
          opacity={0.5}
        />
      )}
    </>
  );
};
